import React, { createContext, useState, useContext, useEffect } from 'react';
import { defaultTemplates } from '../data/defaultTemplates';
import {
  getTemplatesForUser,
  saveTemplatesForUser,
  getSavedMessagesForUser,
  saveMessagesForUser
} from '../services/firestore';
import { useAuth } from '../../../hooks/useAuth';

const TemplateContext = createContext();

export const useTemplates = () => useContext(TemplateContext);

export const TemplateProvider = ({ children }) => {
  const { user } = useAuth();
  const [templateSets, setTemplateSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState('');
  const [savedMessagesByCourse, setSavedMessagesByCourse] = useState({});
  const [loading, setLoading] = useState(true);
  const [templateType, setTemplateType] = useState('SEGUIMIENTO'); // SEGUIMIENTO o VENTA

  // Cargar plantillas del usuario desde Firebase o usar por defecto
  useEffect(() => {
    const loadTemplates = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userTemplates = await getTemplatesForUser(user.workspaceId || user.uid);

        if (userTemplates && userTemplates.length > 0) {
          setTemplateSets(userTemplates);
          setSelectedSetId(userTemplates[0].id);
        } else {
          // Si no hay plantillas del usuario, usar las por defecto
          setTemplateSets(defaultTemplates);
          setSelectedSetId(defaultTemplates[0].id);
        }

        // Cargar mensajes guardados
        const messages = await getSavedMessagesForUser(user.workspaceId || user.uid);
        setSavedMessagesByCourse(messages || {});
      } catch (error) {
        console.error('Error al cargar plantillas:', error);
        // En caso de error, usar plantillas por defecto
        setTemplateSets(defaultTemplates);
        setSelectedSetId(defaultTemplates[0].id);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [user]);

  const addTemplateSet = async (newSet) => {
    try {
      const updated = [...templateSets, newSet];
      setTemplateSets(updated);

      if (user) {
        await saveTemplatesForUser(user.workspaceId || user.uid, updated);
      }
    } catch (error) {
      console.error('Error al agregar plantilla:', error);
    }
  };

  const updateTemplateSet = async (updatedSet) => {
    try {
      const updated = templateSets.map(set =>
        set.id === updatedSet.id ? updatedSet : set
      );
      setTemplateSets(updated);

      if (user) {
        await saveTemplatesForUser(user.workspaceId || user.uid, updated);
      }
    } catch (error) {
      console.error('Error al actualizar plantilla:', error);
    }
  };

  const deleteTemplateSet = async (setId) => {
    try {
      // Prevent deleting the last template set
      if (templateSets.length <= 1) {
        return false;
      }

      const updated = templateSets.filter(set => set.id !== setId);
      setTemplateSets(updated);

      // If the deleted set was selected, select the first available set
      if (selectedSetId === setId) {
        setSelectedSetId(updated[0]?.id || '');
      }

      if (user) {
        await saveTemplatesForUser(user.workspaceId || user.uid, updated);
      }

      return true;
    } catch (error) {
      console.error('Error al eliminar plantilla:', error);
      return false;
    }
  };

  const getSelectedSet = () => {
    return templateSets.find(set => set.id === selectedSetId) || templateSets[0];
  };

  const getTemplatesByType = (type) => {
    return templateSets.filter(set => (set.type || 'SEGUIMIENTO') === type);
  };

  const setCurrentTemplateType = (type) => {
    setTemplateType(type);
  };

  // Funciones para manejar mensajes guardados
  const saveMessagesForCourse = async (courseTitle, messages) => {
    try {
      const updated = {
        ...savedMessagesByCourse,
        [courseTitle]: messages
      };
      setSavedMessagesByCourse(updated);

      if (user) {
        await saveMessagesForUser(user.workspaceId || user.uid, updated);
      }
    } catch (error) {
      console.error('Error al guardar mensajes:', error);
    }
  };

  const getSavedMessagesForCourse = (courseTitle) => {
    return savedMessagesByCourse[courseTitle] || [];
  };

  const getAllSavedCourses = () => {
    return Object.keys(savedMessagesByCourse);
  };

  const deleteSavedMessagesForCourse = async (courseTitle) => {
    try {
      const updated = { ...savedMessagesByCourse };
      delete updated[courseTitle];
      setSavedMessagesByCourse(updated);

      if (user) {
        await saveMessagesForUser(user.workspaceId || user.uid, updated);
      }
    } catch (error) {
      console.error('Error al eliminar mensajes guardados:', error);
    }
  };

  const value = {
    templateSets,
    selectedSetId,
    setSelectedSetId,
    addTemplateSet,
    updateTemplateSet,
    deleteTemplateSet,
    getSelectedSet,
    saveMessagesForCourse,
    getSavedMessagesForCourse,
    getAllSavedCourses,
    deleteSavedMessagesForCourse,
    loading,
    templateType,
    setCurrentTemplateType,
    getTemplatesByType
  };

  return (
    <TemplateContext.Provider value={value}>
      {children}
    </TemplateContext.Provider>
  );
};
